import { Icon, IconButton } from "@material-ui/core";
import React, { Fragment, useEffect, useRef, useState } from "react";
import styled from "styled-components";

import {
    ActionPermissionsObject,
    IWorkspaceResourceFolder,
    WorkspaceResourceFolderType,
    WorkspaceResourceType,
} from "common/interfaces";
import WorkspaceController from "js/controllers/WorkspaceController";
import { app } from "js/namespaces";
import { t } from "js/i18n";
import {
    FolderContainer,
    LibraryFolder,
    selectedClass,
    SideBarLabel,
    StyledAddIcon,
    StyledFoldersSection,
} from "js/react/views/SharedResources/Components/WorkspaceResourcesSidebarHelper";
import {
    promptAndCreateWorkspaceResourceFolder,
    WorkspaceResourceFolderMenuItems,
} from "js/react/views/SharedResources/Components/WorkspaceResourceFolderMenu";
import { IconMenu } from "js/react/components/UiComponents";

const FolderSectionAddButton = styled(IconButton)`
    &&& {
        align-items: center;
        background-color: #e9e9e9;
        border-radius: 50%;
        box-sizing: border-box;
        display: flex;
        flex-shrink: 0;
        height: 20px;
        justify-content: center;
        line-height: 0;
        min-height: 20px;
        min-width: 20px;
        padding: 0;
        width: 20px;

        &:hover {
            background-color: #e9e9e9;

            ${StyledAddIcon} {
                color: #555 !important;
            }
        }
    }
`;

type WorkspaceResourceSideBarFolderProps = {
    folder: IWorkspaceResourceFolder;
    subFolders?: IWorkspaceResourceFolder[];
    isExpanded?: boolean;
    selectedFolderId?: string | null;
    onSelectFolder?: (folderId: string | null) => void;
    onToggleExpanded?: (folderId: string) => void;
    readOnly?: boolean;
    resourceTypes: WorkspaceResourceType[];
    resourceFolders: IWorkspaceResourceFolder[];
    draggedItems?: unknown[];
    onFolderDrop?: (folder: IWorkspaceResourceFolder) => void | Promise<void>;
    onCreateFolder?: (parentFolder?: IWorkspaceResourceFolder) => void | Promise<void>;
};

function WorkspaceResourceSideBarFolder({
    folder,
    subFolders = [],
    isExpanded = false,
    selectedFolderId = null,
    onSelectFolder,
    onToggleExpanded,
    readOnly = false,
    resourceTypes,
    resourceFolders,
    draggedItems,
    onFolderDrop,
    onCreateFolder,
}: WorkspaceResourceSideBarFolderProps) {
    const isSubFolder = Boolean(folder.parentFolderId);
    const hasSubFolders = subFolders.length > 0;
    const ignoreNextFolderMouseUpRef = useRef<boolean>(false);

    const handleMouseUp = async (event: React.MouseEvent | React.TouchEvent) => {
        if (ignoreNextFolderMouseUpRef.current) {
            return;
        }

        if ((draggedItems?.length ?? 0) > 0) {
            event.stopPropagation();
            event.preventDefault();
            await onFolderDrop?.(folder);
            return;
        }

        onSelectFolder?.(folder.id);
    };

    const handleToggleExpanded = (event: React.MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
        onToggleExpanded?.(folder.id);
    };

    return (
        <LibraryFolder
            $nested={isSubFolder}
            data-attr={folder.name}
            data-folder-drop-target="true"
            className={selectedClass(selectedFolderId === folder.id)}
            onMouseUp={handleMouseUp}
            onTouchEnd={handleMouseUp}
        >
            <div
                className={`folder-toggle-icon ${hasSubFolders ? "has-subfolders" : ""}`}
                onMouseUp={hasSubFolders ? handleToggleExpanded : undefined}
            >
                <Icon className="material-symbols-outlined folder-icon">{isSubFolder ? "subdirectory_arrow_right" : "folder"}</Icon>
                {hasSubFolders &&
                    <Icon className="material-symbols-outlined expand-arrow">{isExpanded ? "keyboard_arrow_down" : "keyboard_arrow_right"}</Icon>
                }
            </div>
            <div className="folder-name">{folder.name}</div>
            {!readOnly && !app.isConstrained &&
                <IconMenu onShowMenu={() => {
                    ignoreNextFolderMouseUpRef.current = true;
                    onSelectFolder?.(folder.id);
                }}
                handleCloseMenu={() => {
                    ignoreNextFolderMouseUpRef.current = false;
                }}
                >
                    <WorkspaceResourceFolderMenuItems
                        folder={folder}
                        resourceTypes={resourceTypes}
                        resourceFolders={resourceFolders}
                        selectedFolderId={selectedFolderId}
                        onSelectFolder={onSelectFolder}
                        onCreateFolder={onCreateFolder}
                    />
                </IconMenu>
            }
        </LibraryFolder>
    );
}

export type WorkspaceResourceSideBarFoldersSectionProps = {
    folders: IWorkspaceResourceFolder[];
    selectedFolderId?: string | null;
    onSelectFolder?: (folderId: string | null) => void;
    readOnly?: boolean;
    resourceTypes: WorkspaceResourceType[];
    folderType?: WorkspaceResourceFolderType;
    createResourceType?: WorkspaceResourceType;
    createFolderButtonId?: string;
    shareCreatedFoldersWithDefaultGroups?: boolean;
    draggedItems?: unknown[];
    onFolderDrop?: (folder: IWorkspaceResourceFolder) => void | Promise<void>;
};

export function WorkspaceResourceSideBarFoldersSection({
    folders,
    selectedFolderId = null,
    onSelectFolder,
    readOnly = false,
    resourceTypes,
    folderType = WorkspaceResourceFolderType.SHARED,
    createResourceType,
    createFolderButtonId,
    shareCreatedFoldersWithDefaultGroups = true,
    draggedItems,
    onFolderDrop,
}: WorkspaceResourceSideBarFoldersSectionProps) {
    const resourceFolders = WorkspaceController.resourceFolders;
    const [expandedFolderIds, setExpandedFolderIds] = useState<Set<string>>(() => new Set());
    const canCreateFolders = WorkspaceController.actionPermissions[ActionPermissionsObject.RESOURCE_FOLDERS].create;

    const handleCreateFolder = async (parentFolder?: IWorkspaceResourceFolder) => {
        await promptAndCreateWorkspaceResourceFolder({
            parentFolder,
            resourceTypes,
            resourceFolders,
            folderType,
            createResourceType,
            shareCreatedFoldersWithDefaultGroups,
            onCreated: folder => handleSelectFolder(folder.id),
        });
    };

    useEffect(() => {
        if (!selectedFolderId) {
            return;
        }

        const selectedFolder = resourceFolders.find(folder => folder.id === selectedFolderId);
        const parentFolderId = selectedFolder?.parentFolderId ?? selectedFolder?.id;
        if (!parentFolderId) {
            return;
        }

        setExpandedFolderIds(expandedIds => {
            if (expandedIds.has(parentFolderId)) {
                return expandedIds;
            }

            const nextExpandedIds = new Set(expandedIds);
            nextExpandedIds.add(parentFolderId);
            return nextExpandedIds;
        });
    }, [resourceFolders, selectedFolderId]);

    const handleToggleExpanded = (folderId: string) => {
        setExpandedFolderIds(expandedIds => {
            const nextExpandedIds = new Set(expandedIds);
            if (nextExpandedIds.has(folderId)) {
                nextExpandedIds.delete(folderId);
            } else {
                nextExpandedIds.add(folderId);
            }
            return nextExpandedIds;
        });
    };

    const handleSelectFolder = (folderId: string | null) => {
        if (folderId) {
            setExpandedFolderIds(expandedIds => {
                if (expandedIds.has(folderId)) {
                    return expandedIds;
                }

                const nextExpandedIds = new Set(expandedIds);
                nextExpandedIds.add(folderId);
                return nextExpandedIds;
            });
        }

        onSelectFolder?.(folderId);
    };

    return (
        <StyledFoldersSection>
            <SideBarLabel>
                <span>{t("Folders")}</span>
                {!readOnly && !app.isConstrained && canCreateFolders &&
                    <FolderSectionAddButton size="small" id={createFolderButtonId} onClick={() => handleCreateFolder()}>
                        <StyledAddIcon>add</StyledAddIcon>
                    </FolderSectionAddButton>
                }
            </SideBarLabel>
            <FolderContainer>
                {folders.map(parentFolder => {
                    const foldersForType = resourceFolders.filter(f => f.resourceTypes.some(resourceType => parentFolder.resourceTypes.includes(resourceType)));
                    const subFolders = foldersForType
                        .filter(f => f.parentFolderId === parentFolder.id)
                        .sort((a, b) => a.name.localeCompare(b.name));
                    const folderRowProps = {
                        selectedFolderId,
                        onSelectFolder: handleSelectFolder,
                        onToggleExpanded: handleToggleExpanded,
                        readOnly,
                        resourceTypes,
                        resourceFolders: foldersForType,
                        draggedItems,
                        onFolderDrop,
                        onCreateFolder: handleCreateFolder,
                    };

                    return (
                        <Fragment key={parentFolder.id}>
                            <WorkspaceResourceSideBarFolder
                                folder={parentFolder}
                                subFolders={subFolders}
                                isExpanded={expandedFolderIds.has(parentFolder.id)}
                                {...folderRowProps}
                            />
                            {expandedFolderIds.has(parentFolder.id) && subFolders.map(subFolder => (
                                <WorkspaceResourceSideBarFolder
                                    key={subFolder.id}
                                    folder={subFolder}
                                    {...folderRowProps}
                                />
                            ))}
                        </Fragment>
                    );
                })}
            </FolderContainer>
        </StyledFoldersSection>
    );
}
